import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  TableSortLabel,
  Button
} from '@mui/material';
import { ArrowUpward, ArrowDownward, PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';


interface Verpleegkundige {
  id: number;
  voornaam: string;
  naam: string;
  email: string;
  telefoonnummer: string;
}

type SortField = 'voornaam' | 'naam' | 'email' | 'telefoonnummer';
type SortDirection = 'asc' | 'desc';

const Verpleegkundigen: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('voornaam');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [verpleegkundigen, setVerpleegkundigen] = useState<Verpleegkundige[]>([]);

  useEffect(() => {
    const fetchNurses = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const response = await fetch(`${API_BASE}/nurses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();

        if (!response.ok) {
          return alert(data.message || "Kon verpleegkundigen niet laden");
        }

        setVerpleegkundigen(
          data.map((item: any) => ({
            id: item.id,
            voornaam: item.name?.split(" ")[0] || "",
            naam: item.name?.split(" ").slice(1).join(" ") || "",
            email: item.email,
            telefoonnummer: item.phone || ""
          }))
        );
      } catch (error) {
        console.error(error);
        alert("Server fout bij laden verpleegkundigen");
      }
    };

    fetchNurses();
    document.title = "Verpleegkundigen - Thuisverpleging Portaal";
  }, []);




  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleNieuwGebruiker = () => {
    navigate('/verpleegkundigen/nieuw');
  };

  const handleVerpleegkundigeClick = (verpleegkundige: Verpleegkundige) => {
    navigate(`/verpleegkundigen/${verpleegkundige.id}/${verpleegkundige.voornaam}/${verpleegkundige.naam}`);
  };

  const filteredAndSortedData = verpleegkundigen
    .filter(person => {
      if (!searchTerm.trim()) return true; // Show all if search is empty
      
      const searchLower = searchTerm.toLowerCase().trim();
      
      // Search in all fields
      const searchableText = [
        person.voornaam,
        person.naam,
        person.email,
        person.telefoonnummer.replace(/[\/\-\s]/g, '') // Remove formatting from phone
      ].join(' ').toLowerCase();
      
      return searchableText.includes(searchLower);
    })
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={true} onClose={() => {}} />
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3,
          mt: { xs: '60px', md: 0 },
          ml: { xs: 0, md: '70px' }
        }}
      >
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ color: '#4aa8b4' }}>
              Verpleegkundigen
            </Typography>
            {/* Desktop knop */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={handleNieuwGebruiker}
                sx={{
                  backgroundColor: '#4aa8b4',
                  '&:hover': {
                    backgroundColor: '#3a8b94'
                  }
                }}
              >
                Nieuwe gebruiker
              </Button>
            </Box>
          </Box>

          {/* Search Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Zoeken in verpleegkundigen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ maxWidth: 250 }}
            />
          </Box>

          {/* Mobiele knop - alleen zichtbaar op kleine schermen */}
          <Box sx={{ mb: 3, display: { xs: 'block', sm: 'none' } }}>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={handleNieuwGebruiker}
              sx={{
                backgroundColor: '#4aa8b4',
                '&:hover': {
                  backgroundColor: '#3a8b94'
                }
              }}
            >
              Nieuwe gebruiker
            </Button>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'voornaam'}
                      direction={sortField === 'voornaam' ? sortDirection : 'asc'}
                      onClick={() => handleSort('voornaam')}
                      sx={{ 
                        fontWeight: 'bold',
                        color: '#4aa8b4',
                        '&.Mui-active': { color: '#4aa8b4' },
                        '& .MuiTableSortLabel-icon': { color: '#4aa8b4 !important' }
                      }}
                    >
                      Voornaam
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'naam'}
                      direction={sortField === 'naam' ? sortDirection : 'asc'}
                      onClick={() => handleSort('naam')}
                      sx={{ 
                        fontWeight: 'bold',
                        color: '#4aa8b4',
                        '&.Mui-active': { color: '#4aa8b4' },
                        '& .MuiTableSortLabel-icon': { color: '#4aa8b4 !important' }
                      }}
                    >
                      Naam
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'email'}
                      direction={sortField === 'email' ? sortDirection : 'asc'}
                      onClick={() => handleSort('email')}
                      sx={{ 
                        fontWeight: 'bold',
                        color: '#4aa8b4',
                        '&.Mui-active': { color: '#4aa8b4' },
                        '& .MuiTableSortLabel-icon': { color: '#4aa8b4 !important' }
                      }}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'telefoonnummer'}
                      direction={sortField === 'telefoonnummer' ? sortDirection : 'asc'}
                      onClick={() => handleSort('telefoonnummer')}
                      sx={{ 
                        fontWeight: 'bold',
                        color: '#4aa8b4',
                        '&.Mui-active': { color: '#4aa8b4' },
                        '& .MuiTableSortLabel-icon': { color: '#4aa8b4 !important' }
                      }}
                    >
                      Telefoonnummer
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedData.map((person) => (
                  <TableRow 
                    key={person.id}
                    sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell sx={{ color: '#4aa8b4', fontWeight: 500 }}>
                      <Button
                        onClick={() => handleVerpleegkundigeClick(person)}
                        sx={{
                          color: '#4aa8b4',
                          fontWeight: 500,
                          textTransform: 'none',
                          padding: 0,
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {person.voornaam}
                      </Button>
                    </TableCell>
                    <TableCell>{person.naam}</TableCell>
                    <TableCell>
                      <a 
                        href={`mailto:${person.email}`} 
                        style={{ 
                          color: '#4aa8b4', 
                          textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'underline'}
                        onMouseLeave={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'none'}
                      >
                        {person.email}
                      </a>
                    </TableCell>
                    <TableCell>{person.telefoonnummer}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Results count */}
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="body2" color="textSecondary">
              {filteredAndSortedData.length} van {verpleegkundigen.length} verpleegkundigen
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Verpleegkundigen;